// Free-tier session tracking + cached-only analysis matcher for unauthenticated landing visitors.

import { supabase } from "@/integrations/supabase/client";

// ---- Tunable constants ----
export const FREE_LIMIT = 3;
export const SIMILARITY_THRESHOLD = 0.28;       // weak fuzzy floor (fallback)
export const STRONG_FUZZY_THRESHOLD = 0.55;     // near-exact-paste protection
export const WEAK_MATCH_THRESHOLD = 0.15;       // legacy (unused)
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const STATE_KEY = "greyrock_free_session";

export type FreeState = {
  count: number;
  expiresAt: number;
  sessionId: string;
};

const newSessionId = () =>
  (typeof crypto !== "undefined" && (crypto as any).randomUUID
    ? (crypto as any).randomUUID()
    : `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);

export const getFreeState = (): FreeState => {
  if (typeof window === "undefined") {
    return { count: 0, expiresAt: Date.now() + SESSION_TTL_MS, sessionId: "ssr" };
  }
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as FreeState;
      if (parsed.expiresAt > Date.now()) return parsed;
    }
  } catch {}
  const fresh: FreeState = {
    count: 0,
    expiresAt: Date.now() + SESSION_TTL_MS,
    sessionId: newSessionId(),
  };
  localStorage.setItem(STATE_KEY, JSON.stringify(fresh));
  return fresh;
};

export const peekFreeRemaining = (): number => {
  const s = getFreeState();
  return Math.max(0, FREE_LIMIT - s.count);
};

export const consumeFreeCredit = (): FreeState => {
  const s = getFreeState();
  const next: FreeState = { ...s, count: Math.min(FREE_LIMIT, s.count + 1) };
  localStorage.setItem(STATE_KEY, JSON.stringify(next));
  return next;
};

// Shown when a visitor hits Analyze without typing anything. It is our sample
// text, not their message, so running it must not use up one of their previews.
export const BUILT_IN_EXAMPLE = "You're too sensitive.";

export const shouldChargeForAnalysis = (typedText: string): boolean =>
  typedText.trim().length > 0;

// ---- Cache fetching ----
export type CachedEntry = {
  id: string;
  toxic_message: string;
  toxicity_score: number;
  analysis_logic: string | null;
  teaser_text: string | null;
  category: string | null;
  title: string | null;
};

let cachePromise: Promise<CachedEntry[]> | null = null;

export const loadCache = (): Promise<CachedEntry[]> => {
  if (cachePromise) return cachePromise;
  cachePromise = (async () => {
    const { data, error } = await supabase
      .from("content_library")
      .select("id, toxic_message, toxicity_score, analysis_logic, teaser_text, category, title")
      .is("user_id", null)
      .limit(500);
    if (error || !data) return [];
    return data as CachedEntry[];
  })();
  return cachePromise;
};

// ---- Matching ----
export const normalize = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[^a-z0-9\s']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const tokens = (s: string): string[] => normalize(s).split(" ").filter(Boolean);

const bigrams = (s: string): Set<string> => {
  const n = normalize(s).replace(/\s+/g, " ");
  const set = new Set<string>();
  for (let i = 0; i < n.length - 1; i++) set.add(n.slice(i, i + 2));
  return set;
};

const jaccard = <T,>(a: Set<T>, b: Set<T>): number => {
  if (!a.size && !b.size) return 0;
  let inter = 0;
  a.forEach((v) => b.has(v) && inter++);
  return inter / (a.size + b.size - inter);
};

const similarity = (a: string, b: string): number => {
  const ta = new Set(tokens(a));
  const tb = new Set(tokens(b));
  const tokenScore = jaccard(ta, tb);
  const bigramScore = jaccard(bigrams(a), bigrams(b));
  return tokenScore * 0.6 + bigramScore * 0.4;
};

export type FallbackFamily =
  | "guilt_obligation"
  | "blame_reversal"
  | "reality_distortion"
  | "identity_attack"
  | "triangulation"
  | "pressure_control";

export type MatchResult =
  | { matchType: "exact"; entry: CachedEntry; score: number }
  | { matchType: "fuzzy"; entry: CachedEntry; score: number }
  | {
      matchType: "weak";
      entry: null;
      score: number;
      family: FallbackFamily;
      displayLabel: string;
      articulation: string;
    }
  | { matchType: "generic"; entry: null; score: number };

// Map raw content_library.category values to fallback families.
// Unmapped / vague categories (Relationships, Workplace, NULL) route to GENERIC.
const CATEGORY_FAMILY_MAP: Record<string, FallbackFamily> = {
  "guilt induction": "guilt_obligation",
  "darvo": "blame_reversal",
  "reverse blame": "blame_reversal",
  "ambiguous_responsibility": "blame_reversal",
  "ambiguous responsibility": "blame_reversal",
  "gaslighting": "reality_distortion",
  "invalidation": "reality_distortion",
  "character attacks": "identity_attack",
  "contempt": "identity_attack",
  "triangulation": "triangulation",
  "coercive pressure": "pressure_control",
};

export const categoryToFamily = (raw: string | null | undefined): FallbackFamily | null => {
  if (!raw) return null;
  const key = raw.trim().toLowerCase();
  return CATEGORY_FAMILY_MAP[key] ?? null;
};

// Free-tier analysis runs ENTIRELY in the browser. The user's message is
// never sent to any server or edge function. Honors the homepage promise:
// "Analyzed privately in your browser — your message is never sent to a server."
//
// Local pipeline:
//   1. exact match against cached public library entries (cache was fetched
//      anonymously without the user's text)
//   2. strong fuzzy match against same cache
//   3. weighted SIGNAL_VARIANTS scoring (local dictionary below)
//   4. weak fuzzy match → family fallback
//   5. generic fallback
export const matchCachedAnalysis = async (input: string): Promise<MatchResult> => {
  const normalized = normalize(input);
  if (!normalized) return { matchType: "generic", entry: null, score: 0 };

  const cache = await loadCache().catch(() => [] as CachedEntry[]);

  // 1. Exact
  for (const entry of cache) {
    if (normalize(entry.toxic_message) === normalized) {
      return { matchType: "exact", entry, score: 1 };
    }
  }

  // 2. Best fuzzy candidate
  let bestFuzzy: { entry: CachedEntry; score: number } | null = null;
  for (const entry of cache) {
    const score = similarity(entry.toxic_message, input);
    if (!bestFuzzy || score > bestFuzzy.score) bestFuzzy = { entry, score };
  }
  if (bestFuzzy && bestFuzzy.score >= STRONG_FUZZY_THRESHOLD) {
    return { matchType: "fuzzy", entry: bestFuzzy.entry, score: bestFuzzy.score };
  }

  // 3. Weighted signal variants (local)
  const variantMatch = scoreSignalVariants(normalized);
  if (variantMatch) {
    return {
      matchType: "weak",
      entry: null,
      score: variantMatch.score,
      family: variantMatch.family,
      displayLabel: variantMatch.displayLabel,
      articulation: variantMatch.articulation,
    };
  }

  // 4. Weak fuzzy → family fallback via category
  if (bestFuzzy && bestFuzzy.score >= SIMILARITY_THRESHOLD) {
    const family = categoryToFamily(bestFuzzy.entry.category);
    if (family) {
      return {
        matchType: "weak",
        entry: null,
        score: bestFuzzy.score,
        family,
        displayLabel: FAMILY_LABEL[family],
        articulation: FAMILY_COPY[family],
      };
    }
  }

  // 5. Generic
  return { matchType: "generic", entry: null, score: 0 };
};

// ---- Family fallback copy (articulation register) ----
export const FAMILY_LABEL: Record<FallbackFamily, string> = {
  guilt_obligation: "Guilt & Obligation",
  blame_reversal: "Blame Reversal",
  reality_distortion: "Reality Distortion",
  identity_attack: "Identity Attack",
  triangulation: "Triangulation",
  pressure_control: "Pressure & Control",
};

export const FAMILY_COPY: Record<FallbackFamily, string> = {
  guilt_obligation:
    "You may have come into this with a real concern and left feeling like the ungrateful one. The conversation moved off what happened and onto what you owe — and somewhere in there, your original point quietly disappeared. That shift is the mechanism: when disagreement gets reframed as ingratitude, boundaries start to feel like betrayal.",
  blame_reversal:
    "You might have finished this feeling like the person who needed to apologize, despite starting it with a legitimate concern. The issue you raised got turned around until you were defending yourself instead. When the person who raised the problem becomes the problem, that reversal is the thing to notice — not your reaction to it.",
  reality_distortion:
    "You may have walked away less sure of what you actually experienced than when you started. The conversation didn't address the thing that happened — it quietly questioned whether it happened at all, or whether you're reading it right. That erosion of your own certainty is the effect worth paying attention to.",
  identity_attack:
    "This may have felt less like feedback on something you did and more like a verdict on who you are. When a specific moment gets converted into a permanent trait, there's nothing left to resolve — you can't fix a personality. That move, from 'you did' to 'you are,' is the shift underneath the sting.",
  triangulation:
    "You might be left comparing yourself to someone else who was brought into a conversation that was supposed to be about you. Introducing a third person — their approval, their example, their side — moves the ground out from under the original issue. The comparison isn't the point; the redirection is.",
  pressure_control:
    "You may have felt the room narrowing — like there was a right answer you were being steered toward, and resisting it would cost you. The pressure here isn't always loud. It's the sense that disagreeing isn't really an option. That narrowing of your choices is the thing to name.",
};

export const GENERIC_FALLBACK_COPY =
  "No manipulation pattern found. Nothing in this message matches the patterns the free preview checks for. If you're trying to make sense of how someone else responded, paste their message instead. A full analysis reads tone and the whole conversation, not just individual phrases.";

// ---- Tactic label derivation from sparse cache fields (Tier 1 only) ----
export const deriveTacticLabel = (entry: CachedEntry): string => {
  if (entry.category && entry.category.trim() && entry.category !== "Relationships") {
    return entry.category.toUpperCase();
  }
  if (entry.title && entry.title.trim()) {
    return entry.title.toUpperCase().slice(0, 40);
  }
  // Crude inference from score
  if (entry.toxicity_score >= 8.5) return "REALITY DISTORTION";
  if (entry.toxicity_score >= 7) return "MANIPULATIVE PRESSURE";
  if (entry.toxicity_score >= 4) return "BEHAVIORAL PATTERN";
  return "AMBIGUOUS SIGNAL";
};

// ---- Weighted-signal variant system (self-contained) ----
// Every variant is evaluated against the full message; all matches are
// collected and ranked by severity tier (1 = most severe). Ties within a
// tier are broken by whichever trigger phrase appears earliest in the
// message, then by raw score.
export type SignalVariant = {
  displayLabel: string;              // user sees: "This resembles: {displayLabel}"
  signals: Record<string, number>;   // keyword phrase -> weight
  threshold: number;                 // min summed score to fire
  articulation: string;
  tier: 1 | 2 | 3 | 4 | 5;           // severity tier (1 highest)
};

// Anchor-phrase keys are stored as Base64 to prevent trivial copy-paste
// extraction from the compiled JS bundle. Decoded on-the-fly in local memory
// only when scoreSignalVariants runs — never sent to a server.
const _decodeKey = (b64: string): string => {
  try {
    if (typeof atob === "function") return atob(b64);
    // Node / SSR fallback
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (globalThis as any).Buffer.from(b64, "base64").toString("utf-8");
  } catch {
    return "";
  }
};

export const SIGNAL_VARIANTS: Record<FallbackFamily, SignalVariant[]> = {
  guilt_obligation: [
    {
      displayLabel: "Leveraging Past Sacrifice",
      threshold: 7,
      tier: 4,
      signals: {
        "YWZ0ZXIgZXZlcnl0aGluZw==": 5,
        "ZXZlcnl0aGluZyBpJ3ZlIGRvbmU=": 5,
        "ZG9uZSBmb3IgeW91": 5,
        "YWxsIGkndmUgZG9uZQ==": 5,
        "ZXZlcnl0aGluZyBpIGRv": 4,
        "dW5ncmF0ZWZ1bA==": 4,
        "c2FjcmlmaWNl": 3,
        "YXBwcmVjaWF0ZQ==": 2,
        "Z3JhdGVmdWw=": 2,
      },
      articulation: "You may have come into this conversation with something real to raise, then found yourself defending whether you were grateful enough. The focus shifts away from the issue and onto what you supposedly owe because of past effort or sacrifice. What started as a concern can end up feeling like a debt.",
    },
  ],
  identity_attack: [
    {
      displayLabel: "Global Criticism",
      threshold: 6,
      tier: 4,
      signals: {
        "eW91IGFsd2F5cw==": 5,
        "eW91IG5ldmVy": 5,
        "YWx3YXlzIGRvIHRoaXM=": 5,
        "dHlwaWNhbCBvZiB5b3U=": 5,
        "ZXZlcnkgdGltZQ==": 4,
        "dGhpcyBpcyB3aGF0IHlvdQ==": 4,
        "YWx3YXlz": 2,
        "bmV2ZXI=": 2,
      },
      articulation: "You may have started with a specific moment, but left feeling judged as a person. The conversation expands from what happened to what you are supposedly always like. A single issue becomes a verdict on your character.",
    },
  ],
  reality_distortion: [
    {
      displayLabel: "Reaction Reframing",
      threshold: 5,
      tier: 5,
      signals: {
        "dG9vIHNlbnNpdGl2ZQ==": 5,
        "b3ZlcnJlYWN0aW5n": 5,
        "b3ZlciByZWFjdGluZw==": 5,
        "YmVpbmcgZHJhbWF0aWM=": 5,
        "c28gZHJhbWF0aWM=": 5,
        "bWFraW5nIGEgYmlnIGRlYWw=": 5,
        "Ymxvd2luZyB0aGlzIG91dA==": 4,
        "Y2FsbSBkb3du": 3,
        "cmVsYXg=": 2,
      },
      articulation: "You may have walked away questioning whether your reaction was the real problem. Instead of staying with what upset you, the focus shifts toward whether you're being too sensitive, emotional, or difficult. The result can be self-doubt rather than resolution.",
    },
    {
      displayLabel: "Apology Shift",
      threshold: 6,
      tier: 5,
      signals: {
        "c29ycnkgeW91IGZlZWw=": 6,
        "c29ycnkgeW91IHRoaW5r": 6,
        "c29ycnkgaWYgeW91": 5,
        "c29ycnkgdGhhdCB5b3U=": 5,
      },
      articulation: "You may have received something that sounds like an apology without feeling truly understood. The attention moves quickly from what happened to how you reacted to it. That can leave the original concern untouched, even though the conversation appears resolved.",
    },
    {
      displayLabel: "Benchmarking Against Others",
      threshold: 5,
      tier: 5,
      signals: {
        "bm9ib2R5IGVsc2U=": 5,
        "ZXZlcnlvbmUgZWxzZQ==": 5,
        "bm8gb25lIGVsc2U=": 5,
        "ZXZlcnlib2R5IGVsc2U=": 5,
      },
      articulation: "You may have left wondering whether you're allowed to feel the way you do at all. Rather than addressing your concern directly, the comparison shifts to how other people supposedly see it. The discussion becomes about your reaction's legitimacy instead of the issue itself.",
    },
    {
      displayLabel: "Perception Questioning",
      threshold: 6,
      tier: 2,
      signals: {
        "dGhhdCdzIG5vdCB3aGF0IGhhcHBlbmVk": 6,
        "dGhhdCBuZXZlciBoYXBwZW5lZA==": 6,
        // Supporting phrase: only counts when another denial phrase matched.
        "eW91J3JlIGNyYXp5": 0,
        "ZGlkbid0IGhhcHBlbg==": 6,
        "eW91J3JlIHJlbWVtYmVyaW5n": 6,
        "eW91J3JlIGltYWdpbmluZw==": 6,
        "bmV2ZXIgc2FpZCB0aGF0": 6,
        "bWFraW5nIHRoaW5ncyB1cA==": 6,
        "aSBkaWRuJ3Qgc2F5IHRoYXQ=": 6,
        "aSBuZXZlciBkaWQgdGhhdA==": 6,
        "dGhpbmsgaSBzYWlkIHRoYXQ=": 6,
      },
      articulation: "You may have entered the conversation feeling certain about what happened and left feeling less sure of your own memory or interpretation. The disagreement moves beyond facts and into whether your version of events can be trusted. Confusion often appears where confidence used to be.",
    },
  ],
  pressure_control: [
    {
      displayLabel: "Withdrawal Pressure",
      threshold: 5,
      tier: 3,
      signals: {
        "aSBndWVzcyBpJ2xs": 5,
        "c3RvcCB0cnlpbmc=": 5,
        "aSB3b24ndCBib3RoZXI=": 5,
        "bm90aGluZyBpIGRv": 5,
        "a2VlcCBteSB0aG91Z2h0cyB0byBteXNlbGY=": 5,
        "ZG8gd2hhdGV2ZXIgeW91IHdhbnQ=": 4,
        "Zm9yZ2V0IGl0IHRoZW4=": 4,
        "ZmluZSB0aGVu": 3,
        "aSBnaXZlIHVw": 3,
      },
      articulation: "You may have found the conversation suddenly turning toward resignation, distance, or giving up. Instead of working through the issue, the message creates pressure to stop the withdrawal or repair the relationship yourself. The focus shifts from the concern to the threat of disconnection.",
    },
  ],
  triangulation: [
    {
      displayLabel: "Appealing to Consensus",
      threshold: 5,
      tier: 5,
      signals: {
        "ZXZlcnlvbmUgdGhpbmtz": 5,
        "ZXZlcnlvbmUgYWdyZWVz": 5,
        "YXNrIGFueW9uZQ==": 5,
        "ZXZlcnlib2R5IGtub3dz": 5,
        "b3RoZXIgcGVvcGxl": 3,
      },
      articulation: "You may have felt outnumbered even though only one person was speaking. References to what everyone thinks or what other people would say can make a concern feel isolated before it has been addressed. The weight comes from imagined agreement rather than the issue itself.",
    },
  ],
  blame_reversal: [
    {
      displayLabel: "Turning It Around",
      threshold: 6,
      tier: 3,
      signals: {
        "eW91IHN0YXJ0ZWQ=": 6,
        "eW91ciBmYXVsdA==": 6,
        "aWYgeW91IGhhZG4ndA==": 6,
        "eW91IG1hZGUgbWU=": 6,
        "eW91IHR3aXN0IGV2ZXJ5dGhpbmc=": 6,
        "eW91J3JlIHRoZSBvbmUgd2hv": 6,
        "aSdtIHRoZSBvbmUgd2hvIHNob3VsZCBiZSB1cHNldA==": 6,
        "eW91IGFsd2F5cyBtYWtlIG1lIHRoZSBiYWQgZ3V5": 6,
        "bG9vayB3aGF0IHlvdQ==": 4,
      },
      articulation: "You may have entered the conversation with a concern and left defending yourself instead. Responsibility shifts away from the original issue and toward something you supposedly caused, triggered, or started. The conversation changes direction until the person raising the concern becomes the one under scrutiny.",
    },
  ],
};

export type SignalVariantMatch = {
  family: FallbackFamily;
  variant: SignalVariant;
  score: number;
  displayLabel: string;
  articulation: string;
};

// Evaluates EVERY variant against the full message and collects all matches.
// The winner is the most severe tier; ties within a tier go to the variant
// whose trigger phrase appears earliest in the message, then to the higher
// raw score.
export function scoreSignalVariants(normalizedInput: string): SignalVariantMatch | null {
  let best: (SignalVariantMatch & { tier: number; earliest: number }) | null = null;
  (Object.keys(SIGNAL_VARIANTS) as FallbackFamily[]).forEach((family) => {
    for (const variant of SIGNAL_VARIANTS[family]) {
      let score = 0;
      let earliest = Infinity;
      let supportingHit = false;
      for (const [encodedPhrase, weight] of Object.entries(variant.signals)) {
        const phrase = _decodeKey(encodedPhrase);
        if (!phrase) continue;
        const idx = normalizedInput.indexOf(phrase);
        if (idx === -1) continue;
        // Weight 0 = supporting phrase: never fires on its own, only adds a
        // small bonus when another phrase in the same variant already matched.
        if (weight === 0) {
          supportingHit = true;
          continue;
        }
        score += weight;
        if (idx < earliest) earliest = idx;
      }
      if (supportingHit && score > 0) score += 2;
      if (score < variant.threshold) continue;
      if (
        !best ||
        variant.tier < best.tier ||
        (variant.tier === best.tier && earliest < best.earliest) ||
        (variant.tier === best.tier && earliest === best.earliest && score > best.score)
      ) {
        best = {
          family,
          variant,
          score,
          displayLabel: variant.displayLabel,
          articulation: variant.articulation,
          tier: variant.tier,
          earliest,
        };
      }
    }
  });
  return best;
}


// ---- Routing-outcome logging (privacy-safe; no message content) ----
export type RoutingOutcome = {
  matchType: "exact" | "fuzzy_strong" | "variant" | "fuzzy_weak" | "generic";
  displayLabel: string | null;
  family: string | null;
  timestamp: string;
};

export const getRoutingOutcome = (result: MatchResult): RoutingOutcome => {
  const matchType: RoutingOutcome["matchType"] =
    result.matchType === "exact"
      ? "exact"
      : result.matchType === "weak"
      ? "variant"
      : result.matchType === "generic"
      ? "generic"
      : result.score >= STRONG_FUZZY_THRESHOLD
      ? "fuzzy_strong"
      : "fuzzy_weak";

  return {
    matchType,
    displayLabel: result.matchType === "weak" ? result.displayLabel : null,
    family: result.matchType === "weak" ? result.family : null,
    timestamp: new Date().toISOString(),
  };
};
